import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import Login from '../pages/Login';
import {
  SparklesIcon,
  CreditCardIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-whatsapp-50 via-green-50 to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-xl animate-pulse"></div>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-whatsapp-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading Snwolley Business API...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return children;
};

// Premium Route for trial users blocking
export const PremiumRoute = ({ children, feature = "this feature", requireScale = false }) => {
  const { isAuthenticated, loading } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await api.getSubscriptionStatus();
        const widgetData = response.data.widget;
        setSubscriptionStatus(widgetData);
      } catch (error) {
        console.error('Error checking subscription:', error);
      } finally {
        setCheckingSubscription(false);
      }
    };

    if (isAuthenticated) {
      checkSubscription();
    }
  }, [isAuthenticated]);

  if (loading || checkingSubscription) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-whatsapp-50 via-green-50 to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-xl animate-pulse"></div>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-whatsapp-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading Snwolley Business API...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  // Check if user should be blocked based on subscription level
  const isBlocked = () => {
    const status = subscriptionStatus?.subscription?.status;
    const packageName = subscriptionStatus?.subscription?.package_name?.toLowerCase();
    
    // Always block trial users
    if (status === 'trial') {
      return true;
    }
    
    // For features that require Scale tier or higher, also block Spark users
    if (requireScale && packageName === 'spark') {
      return true;
    }
    
    return false;
  };

  // Block users based on subscription level
  if (isBlocked()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            {/* Premium Icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-orange-400 to-pink-500 mb-6">
              <SparklesIcon className="h-8 w-8 text-white" />
            </div>

            {/* Header */}
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Premium Feature</h1>
            <p className="text-gray-600 mb-6">
              {feature} is available for subscribed users only.
            </p>

            {/* Trial Info */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-left">
                  <p className="text-sm font-medium text-orange-800">Trial Period Active</p>
                  <p className="text-sm text-orange-700 mt-1">
                    {requireScale 
                      ? 'Upgrade to Scale or higher to unlock API access and advanced features.'
                      : 'Upgrade to unlock direct message sending and other premium features.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => navigate('/dashboard/billing/subscription')}
                className="w-full bg-gradient-to-r from-whatsapp-500 to-green-600 text-white px-6 py-3 rounded-lg font-medium hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg"
              >
                <div className="flex items-center justify-center">
                  <CreditCardIcon className="h-5 w-5 mr-2" />
                  Upgrade Now
                </div>
              </button>

              <button
                onClick={() => navigate('/dashboard/')}
                className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                <div className="flex items-center justify-center">
                  <ArrowLeftIcon className="h-5 w-5 mr-2" />
                  Back to Dashboard
                </div>
              </button>
            </div>

            {/* Footer */}
            <p className="text-xs text-gray-500 mt-6">
              Need help? <a href="mailto:marketing@npontu.com" className="text-blue-600 hover:text-blue-800 underline">Contact our support team</a> for assistance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
